import { Check, CornerDownRight, MessageSquare, RotateCcw, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { can, COMMENT_MAX_CHARS, type CommentThread } from "@ide/shared";
import type { SuggestionsHandle } from "@/collab/session";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlay";
import { Textarea } from "@/components/ui/primitives";
import { cn, timeAgo } from "@/lib/utils";
import { useRole, useTree, useWs } from "./context";
import { useWorkspace } from "./store";

/** Conversas do arquivo aberto: a linha atual de cada uma e o canal para pedir mudanças. */
interface FileComments {
  threads: CommentThread[];
  lines: Record<string, number>;
  request: SuggestionsHandle["request"];
}

export const useComments = create<{ files: Record<string, FileComments> }>(() => ({ files: {} }));

const refs = new Map<string, number>();

/** Publica as conversas de um arquivo enquanto algum editor dele estiver aberto. */
export function retainComments(fileId: string) {
  refs.set(fileId, (refs.get(fileId) ?? 0) + 1);
  return () => {
    const n = (refs.get(fileId) ?? 1) - 1;
    if (n > 0) return void refs.set(fileId, n);
    refs.delete(fileId);
    useComments.setState((s) => {
      const { [fileId]: _gone, ...rest } = s.files;
      return { files: rest };
    });
  };
}

export function publishComments(fileId: string, data: FileComments) {
  useComments.setState((s) => ({ files: { ...s.files, [fileId]: data } }));
}

/** Conversas abertas (não resolvidas) no arquivo ativo. */
export function useCommentCount() {
  const active = useWorkspace((s) => s.active);
  return useComments((s) =>
    active ? (s.files[active]?.threads.filter((t) => !t.resolvedAt).length ?? 0) : 0,
  );
}

async function send(file: FileComments | undefined, body: Parameters<SuggestionsHandle["request"]>[0]) {
  if (!file) return false;
  const res = await file.request(body);
  if (!res.ok) toast.error(res.error ?? "Não foi possível salvar o comentário.");
  return res.ok;
}

function Composer({
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  autoFocus,
}: {
  placeholder: string;
  submitLabel: string;
  onSubmit: (text: string) => Promise<boolean>;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit(text.trim());
    setBusy(false);
    if (ok) setText("");
  };
  return (
    <div className="space-y-1.5">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            void submit();
          } else if (e.key === "Escape" && onCancel) {
            e.preventDefault();
            onCancel();
          }
        }}
        rows={2}
        maxLength={COMMENT_MAX_CHARS}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="min-h-[52px] resize-none text-[12.5px]"
        aria-label={placeholder}
      />
      <div className="flex items-center justify-end gap-1.5">
        <span className="mr-auto text-[11px] text-fg-subtle">Ctrl+Enter para enviar</span>
        {onCancel ? (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="primary"
          loading={busy}
          disabled={!text.trim()}
          onClick={() => void submit()}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function ThreadView({ thread, file }: { thread: CommentThread; file: FileComments | undefined }) {
  const { me } = useWs();
  const role = useRole();
  const canTalk = can(role, "suggest");
  const canDelete = (authorId: string) => authorId === me.user.id || can(role, "approve");
  const resolved = Boolean(thread.resolvedAt);
  const [replying, setReplying] = useState(false);
  return (
    <div className={cn("rounded-lg border border-border bg-surface", resolved && "opacity-80")}>
      <div className="flex items-start gap-2 px-3 pt-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-[12px]">
            <span className="font-semibold text-fg">{thread.authorName}</span>{" "}
            <span className="text-fg-subtle">· {timeAgo(thread.createdAt)}</span>
            {resolved ? (
              <span className="ml-1.5 text-fg-subtle">· resolvida por {thread.resolvedByName}</span>
            ) : null}
          </p>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-fg">
            {thread.text}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {canTalk ? (
            <Tip label={resolved ? "Reabrir" : "Resolver"}>
              <button
                onClick={() =>
                  void send(file, { type: "comment.resolve", id: thread.id, resolved: !resolved })
                }
                className="flex h-6 w-6 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                aria-label={resolved ? "Reabrir conversa" : "Resolver conversa"}
              >
                {resolved ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
              </button>
            </Tip>
          ) : null}
          {canDelete(thread.authorId) ? (
            <Tip label="Apagar conversa">
              <button
                onClick={() => void send(file, { type: "comment.delete", id: thread.id })}
                className="flex h-6 w-6 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
                aria-label="Apagar conversa"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </Tip>
          ) : null}
        </div>
      </div>
      {thread.replies.length ? (
        <ul className="mt-1.5 space-y-1.5 border-t border-border px-3 py-2">
          {thread.replies.map((r) => (
            <li key={r.id} className="group flex gap-2">
              <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />
              <div className="min-w-0 flex-1">
                <p className="text-[12px]">
                  <span className="font-semibold text-fg">{r.authorName}</span>{" "}
                  <span className="text-fg-subtle">· {timeAgo(r.createdAt)}</span>
                </p>
                <p className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-fg">
                  {r.text}
                </p>
              </div>
              {canDelete(r.authorId) ? (
                <button
                  onClick={() => void send(file, { type: "comment.delete", id: thread.id, replyId: r.id })}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-subtle opacity-0 hover:bg-bg-muted hover:text-fg focus-visible:opacity-100 group-hover:opacity-100"
                  aria-label="Apagar resposta"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {canTalk ? (
        <div className="px-3 pb-2.5 pt-1.5">
          {replying ? (
            <Composer
              autoFocus
              placeholder={resolved ? "Responder (reabre a conversa)…" : "Responder…"}
              submitLabel="Responder"
              onSubmit={async (text) => {
                const ok = await send(file, { type: "comment.reply", id: thread.id, text });
                if (ok) setReplying(false);
                return ok;
              }}
              onCancel={() => setReplying(false)}
            />
          ) : (
            <button
              onClick={() => setReplying(true)}
              className="text-[12px] text-fg-muted underline-offset-4 hover:text-fg hover:underline"
            >
              Responder
            </button>
          )}
        </div>
      ) : (
        <div className="pb-2" />
      )}
    </div>
  );
}

/**
 * Conversas de uma linha, embaixo dela no editor (como na revisão de código). Sem conversa, já
 * abre o campo para começar uma.
 */
export function CommentZone({
  fileId,
  line,
  anchorOf,
  onClose,
}: {
  fileId: string;
  line: number;
  /** Posição relativa (Yjs) do começo da linha, no momento do envio. */
  anchorOf: (line: number) => string | null;
  onClose: () => void;
}) {
  const role = useRole();
  const file = useComments((s) => s.files[fileId]);
  const threads = (file?.threads ?? []).filter((t) => file?.lines[t.id] === line);
  const [composing, setComposing] = useState(threads.length === 0);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      ref={box}
      className="max-w-[640px] animate-pop-in space-y-2 rounded-xl border border-border-strong bg-surface p-2.5 font-sans shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]"
      role="dialog"
      aria-label={`Comentários da linha ${line}`}
    >
      <div className="flex items-center gap-2 px-0.5">
        <MessageSquare className="h-3.5 w-3.5 text-fg-muted" />
        <span className="text-[12px] font-medium text-fg">Linha {line}</span>
        <span className="text-[11.5px] text-fg-subtle">visível para todos no projeto</span>
        <button
          onClick={onClose}
          className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg"
          aria-label="Fechar comentários"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {threads.map((t) => (
        <ThreadView key={t.id} thread={t} file={file} />
      ))}
      {can(role, "suggest") ? (
        composing ? (
          <Composer
            autoFocus
            placeholder="Comentar nesta linha…"
            submitLabel="Comentar"
            onSubmit={async (text) => {
              const anchor = anchorOf(line);
              if (!anchor) {
                toast.error("Essa linha não existe mais.");
                return false;
              }
              const ok = await send(file, { type: "comment.create", anchor, text });
              if (ok) setComposing(false);
              return ok;
            }}
            onCancel={threads.length ? () => setComposing(false) : onClose}
          />
        ) : (
          <button
            onClick={() => setComposing(true)}
            className="px-0.5 text-[12px] text-fg-muted underline-offset-4 hover:text-fg hover:underline"
          >
            Nova conversa nesta linha
          </button>
        )
      ) : threads.length === 0 ? (
        <p className="px-0.5 text-[12px] text-fg-subtle">Seu papel permite ler, mas não comentar.</p>
      ) : null}
    </div>
  );
}

/** Aba Comentários do painel inferior: as conversas do arquivo aberto, por linha. */
export function CommentsList() {
  const active = useWorkspace((s) => s.active);
  const tree = useTree();
  const file = useComments((s) => (active ? s.files[active] : undefined));
  const [showResolved, setShowResolved] = useState(false);
  const path = active ? tree.paths.get(active) : null;
  if (!active || !path) {
    return (
      <div className="flex flex-1 items-center justify-center text-[12.5px] text-fg-subtle">
        Abra um arquivo para ver os comentários.
      </div>
    );
  }
  const all = [...(file?.threads ?? [])].sort((a, b) => (file?.lines[a.id] ?? 0) - (file?.lines[b.id] ?? 0));
  const list = showResolved ? all : all.filter((t) => !t.resolvedAt);
  const resolved = all.length - all.filter((t) => !t.resolvedAt).length;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-3 px-4 text-[12px] text-fg-muted">
        <span className="truncate font-medium">{path}</span>
        <label className="ml-auto flex shrink-0 cursor-pointer items-center gap-1.5">
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          Mostrar resolvidas{resolved ? ` (${resolved})` : ""}
        </label>
      </div>
      {list.length ? (
        <ul className="min-h-0 flex-1 overflow-y-auto pb-2">
          {list.map((t) => (
            <li key={t.id}>
              <button
                onClick={() => useWorkspace.getState().requestComment(active, file?.lines[t.id] ?? t.line)}
                className="flex w-full items-start gap-2.5 px-4 py-1.5 text-left text-[12.5px] hover:bg-bg-muted focus-visible:bg-bg-muted focus-visible:outline-none"
              >
                <MessageSquare
                  className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", t.resolvedAt ? "text-fg-subtle" : "text-fg")}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-fg">{t.authorName}</span>{" "}
                  <span className={cn("text-fg-muted", t.resolvedAt && "line-through")}>{t.text}</span>
                  {t.replies.length ? (
                    <span className="ml-1.5 text-fg-subtle">
                      · {t.replies.length} {t.replies.length === 1 ? "resposta" : "respostas"}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[11.5px] text-fg-subtle">
                  Ln {file?.lines[t.id] ?? t.line}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[12.5px] text-fg-subtle">
          {all.length
            ? "Todas as conversas deste arquivo estão resolvidas."
            : "Nenhum comentário neste arquivo. Clique no + na margem do editor para comentar uma linha."}
        </div>
      )}
    </div>
  );
}
